import type { JWTPayload as BasicJWTPayload } from "jose";
import { decodeJwt } from "jose";
import { api } from "~/lib/api";
import { md5 } from "~/lib/hashing";

export type JWTPayload = BasicJWTPayload & {
    channel: string;
    default_locale_code: string;
    features: Record<string, boolean>;
    handle: string;
    is_admin: boolean;
    is_owner: boolean;
    login_url: string;
    permissions: string[];
    production_id: number;
    production_name: string;
    prv: string;
    staff_email: string;
    staff_id: number;
    user_id: number;
};

const oneMinute = 60 * 1000;

export const useAuthStore = defineStore("authStore", () => {
    const slug = useSlug();

    const { setLocale } = useLocalSettingsStore();
    const { locale_overridden } = useLocalSettingsStore();

    const timer = ref<ReturnType<typeof setTimeout>>();
    const token = useSlugLocalStorage<string | undefined>(
        "production-administration-tokenStore-token",
        undefined,
    );

    const refreshToken = useSlugLocalStorage<string | undefined>(
        "production-administration-tokenStore-refreshToken",
        undefined,
    );

    const refreshTokenTtl = useSlugLocalStorage<string | undefined>(
        "production-administration-tokenStore-refreshTokenTtl",
        undefined,
    );

    const tokenLogoutHash = useSlugLocalStorage<string | undefined>(
        "production-administration-tokenStore-tokenLogoutHash",
        undefined,
    );

    const now = useNow({ interval: oneMinute });

    const isRefreshTokenExpired = computed<boolean>(() => {
        return (
            refreshTokenTtl.value === undefined ||
            new Date(refreshTokenTtl.value).getTime() < now.value.getTime()
        );
    });

    const payload = computed(() => {
        if (!token.value) return;

        try {
            return decodeJwt<JWTPayload>(token.value);
        } catch {
            return;
        }
    });

    const isTokenExpired = computed<boolean>(() => {
        return (
            payload.value?.exp === undefined ||
            payload.value.exp * 1000 - now.value.getTime() < oneMinute
        );
    });

    const isLoggedIn = computed<boolean>(
        () =>
            token.value !== undefined &&
            (!isTokenExpired.value || !isRefreshTokenExpired.value),
    );

    const isLoggedOut = computed<boolean>(
        () =>
            tokenLogoutHash.value !== undefined &&
            md5(token.value ?? "") === tokenLogoutHash.value,
    );

    const loginEndpoint = api.auth.login.use();

    const { mutateAsync: loginCall } = useMutation({
        ...loginEndpoint,
        onError: (error: Error) => {
            return error;
        },
        onSuccess: (data) => {
            setToken({
                refreshToken: data.refresh_token,
                refreshTokenTtl: data.refresh_token_ttl,
                token: data.access_token,
            });

            if (!locale_overridden) {
                setLocale(payload.value?.default_locale_code ?? "en");
            }

            tokenLogoutHash.value = undefined;
        },
    });

    const refreshEndpoint = api.auth.refresh.use();

    const { mutateAsync: refreshCall } = useMutation({
        ...refreshEndpoint,
        onSuccess: (data) => {
            setToken({
                refreshToken: data.refresh_token,
                refreshTokenTtl: data.refresh_token_ttl,
                token: data.access_token,
            });
        },
    });

    async function login(email: string, password: string, slug: string) {
        await loginCall({
            email,
            password,
            slug,
        });

        reloadNuxtApp();
    }

    function logout() {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (!slug && !isLoggedIn.value) {
            return;
        }

        if (token.value) {
            tokenLogoutHash.value = md5(token.value);
        }

        setToken();

        reloadNuxtApp();
    }

    async function refresh() {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (isLoggedOut || !refreshToken.value) {
            return;
        }

        await refreshCall(refreshToken.value);
    }

    function startRefreshTokenTimer() {
        if (timer.value) clearTimeout(timer.value);

        if (!payload.value?.exp) {
            throw new Error('"exp" not set in JWT!');
        }

        const timeout =
            payload.value.exp * 1000 - now.value.getTime() - oneMinute;
        timer.value = setTimeout(() => void refreshAndLogErrors(), timeout);
    }

    async function refreshAndLogErrors() {
        try {
            await refresh();
        } catch (error) {
            console.log(error);
        }
    }

    function setToken(tokenData?: {
        refreshToken?: string;
        refreshTokenTtl?: string;
        token?: string;
    }) {
        refreshToken.value = tokenData?.refreshToken;
        refreshTokenTtl.value = tokenData?.refreshTokenTtl;
        token.value = tokenData?.token;

        if (token.value && payload.value?.exp) startRefreshTokenTimer();
        else if (timer.value) clearTimeout(timer.value);
    }

    // Sync changes to the token or refreshToken in real-time
    watch([token, refreshToken, refreshTokenTtl], ([newToken]) => {
        if (!newToken) {
            return;
        }
    });

    watchEffect(() => {
        if (isTokenExpired.value && !isRefreshTokenExpired.value) {
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            if (isLoggedOut) {
                logout();
            } else {
                void refresh();
            }
        }
    });

    setToken({
        refreshToken: refreshToken.value,
        token: token.value,
    });

    return {
        isLoggedIn,
        isLoggedOut,
        isRefreshTokenExpired,
        isTokenExpired,
        login,
        logout,
        payload: readonly(payload),
        refresh,
        refreshToken: readonly(refreshToken),
        setToken,
        token: readonly(token),
    };
});
